import { Fragment, useState, useEffect } from 'react';
import { Modal } from './Modal';
import { performAction } from '../api/sophia';

// Color palette matching the Treemap component
const COLORS = {
  primary: '#33485B',      // Dark slate - main actions
  primaryLight: '#4a6178', // Lighter slate for hover
  secondary: '#688096',    // Slate blue - secondary elements
  secondaryLight: '#7a92a8',
  success: '#71685F',      // Warm brown - success states
  successLight: '#8C7F75', // Lighter brown
  warning: '#A7674D',      // Rust - validation warnings
  warningLight: '#CE9A85', // Peachy salmon
  error: '#9F6460',        // Dusty rose - errors
  errorLight: '#D4C4C4',   // Light beige
  background: '#f5f0eb',   // Warm off-white background
  backgroundAlt: '#ebe5de', // Slightly darker warm background
};

/**
 * Capitalize a shop name properly (convert ALL CAPS to Title Case)
 * e.g., "MILAN FLAGSHIP STORE" -> "Milan Flagship Store"
 */
function formatShopName(name: string): string {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get the display name for a shop, using the mapping if available
 */
function getShopDisplayName(shopId: string, shopMapping?: Record<string, string>): string {
  const raw = String(shopId || '');
  // SPJ-892: negozi aggregati/gruppi (id "EC1|,|EC2") → mappa ogni componente al suo nome
  // e uniscili (es. "Ecommerce Europa, Ecommerce Usa") invece di mostrare l'id grezzo.
  if (raw.includes('|,|')) {
    const names = raw
      .split('|,|')
      .map((part) => part.replace(/\|/g, '').trim())
      .filter((part) => part.length > 0)
      .map((part) => (shopMapping && shopMapping[part] ? formatShopName(shopMapping[part]) : part));
    if (names.length > 0) return names.join(', ');
  }
  if (shopMapping && shopMapping[shopId]) {
    return formatShopName(shopMapping[shopId]);
  }
  return shopId; // Fallback to shop ID if no mapping
}

function normalizeActionPayload(result: any): any {
  if (result && result.status === 'ok' && result.data && typeof result.data === 'object') {
    return result.data;
  }
  return result;
}

function extractWeborderError(result: any): string | null {
  const payload = normalizeActionPayload(result);
  const response = payload?.response;

  if (payload?.status === 'error') {
    return payload.errstr || response?.message || response?.errstr || response?.error || 'Esecuzione fallita';
  }
  if (payload?.success === false || payload?.error === true) {
    return payload.message || payload.errstr || payload.error_code || 'Esecuzione fallita';
  }
  if (response && (response.success === false || response.error === true)) {
    return response.message || response.errstr || response.error_code || 'Esecuzione fallita';
  }
  return null;
}

interface FieldSchema {
  name: string;
  type: 'date' | 'str' | 'int';
  default?: string | number;
  editable?: boolean;
  must_validate?: boolean;
  description?: string;
}

// Schema is now nested: {shop_id: {sotto_consegna: FieldSchema[]}}
interface OrderSchema {
  [shopId: string]: {
    [sottoConsegna: string]: FieldSchema[];
  };
}

interface CollectionMapping {
  [code: string]: {
    codename: string;
    name: string;
    data_inizio_consegna: string;
    data_fine_consegna: string;
  };
}

interface FieldValue {
  value: string | number;
  validated: boolean;
  modified: boolean;
}

// Values are now triply nested: {shop_id: {sotto_consegna: {field_name: FieldValue}}}
type ValuesState = Record<string, Record<string, Record<string, FieldValue>>>;

// Order object returned from preexecute
interface PreexecuteOrder {
  shop_id: string;
  // Sotto-consegna dell'ordine (es. "Resort 1"): identifica e raggruppa gli ordini qui
  // dentro. Prima questo valore arrivava su `consegna`, sovrascrivendo il codice consegna
  // digitato dall'utente, che cosi' non raggiungeva mai weborder.
  sotto_consegna: string;
  // Codice consegna scelto nella maschera. Non si usa per identificare l'ordine: viaggia
  // e basta, ed e' quello che weborder si aspetta.
  consegna: string;
  n_ordine: string;
  [key: string]: any; // Other fields in the order
}

// Track execution status for each order
interface OrderExecutionState {
  order: PreexecuteOrder;
  status: 'pending' | 'executing' | 'success' | 'error';
  error?: string;
  result?: string;
}

interface SplitPlan {
  id: string;
  sourceOrderKey: string;
  targetShopId: string;
  percentage: number;
  manualTargetAllocation?: Record<string, Record<string, number>>;
}

interface OrderLineStats {
  linesTotal: number;
  linesWithCapi: number;
  totalCapi: number;
  linesChanged: number;
  linesActivated: number;
  linesDeactivated: number;
}

interface DivisionInfo {
  sourceShopId: string;
  targetShopId: string;
  percentage: number;
  role: 'source' | 'target';
}

interface OrderMetaEditorState {
  open: boolean;
  orderIndex: number | null;
  draftOrder: PreexecuteOrder | null;
  title: string;
}

interface SplitEditorState {
  open: boolean;
  planId: string | null;
  sourceOrderKey: string;
  sourceShopId: string;
  targetShopId: string;
  percentage: number;
  baseSkuTaglie: Record<string, Record<string, number>>;
  targetAllocation: Record<string, Record<string, number>>;
}

interface SkuTaglieAdapter {
  skuTaglie: Record<string, Record<string, number>>;
  write: (order: Record<string, unknown>, nextSkuTaglie: Record<string, Record<string, number>>) => void;
}

interface WeborderExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  threadId: string;
  messageId: string;
  optimizationId: string;
}

export function WeborderExportModal({ 
  isOpen, 
  onClose, 
  threadId, 
  messageId, 
  optimizationId 
}: WeborderExportModalProps) {
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState<OrderSchema | null>(null);
  const [collectionMapping, setCollectionMapping] = useState<CollectionMapping | null>(null);
  const [shopIds, setShopIds] = useState<string[]>([]);
  const [allPossibleShopIds, setAllPossibleShopIds] = useState<string[]>([]);
  const [shopMapping, setShopMapping] = useState<Record<string, string> | null>(null);
  // SPJ-892: raggruppamenti negozi per lo split aggregato (uniforme all'export Excel).
  // aggregatedShops = negozi aggregati (rilevati dal risultato grezzo) e loro componenti;
  // shopGroups = mappa id-negozio -> etichetta gruppo (stessa etichetta = un ordine, budget sommato).
  const [aggregatedShops, setAggregatedShops] = useState<Array<{ sourceId: string; members: Array<{ id: string; label: string }> }>>([]);
  const [shopGroups, setShopGroups] = useState<Record<string, string>>({});
  const [appliedShopGroups, setAppliedShopGroups] = useState<Record<string, string>>({});
  const [values, setValues] = useState<ValuesState>({});
  // SPJ-1363: stato della GET date consegna dalla BASP, per chiave `${shopId}::${sottoConsegna}`:
  // deliveryLoading -> spinner in corso; deliveryError -> messaggio rosso (codice non trovato / errore).
  const [deliveryLoading, setDeliveryLoading] = useState<Record<string, boolean>>({});
  const [deliveryError, setDeliveryError] = useState<Record<string, string>>({});
  const [activeShopTab, setActiveShopTab] = useState<string>('');
  const [activeSottoConsegnaTab, setActiveSottoConsegnaTab] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);
  const [preexecuteOrders, setPreexecuteOrders] = useState<OrderExecutionState[]>([]);
  const [basePreexecuteOrders, setBasePreexecuteOrders] = useState<OrderExecutionState[]>([]);
  const [preexecuteError, setPreexecuteError] = useState<string | null>(null);
  const [allOrdersExecuted, setAllOrdersExecuted] = useState(false);
  const [splitPlans, setSplitPlans] = useState<SplitPlan[]>([]);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [expandedOrderSummaries, setExpandedOrderSummaries] = useState<Set<string>>(new Set());
  const [orderLineStatsByOrderKey, setOrderLineStatsByOrderKey] = useState<Record<string, OrderLineStats>>({});
  const [divisionInfoByOrderKey, setDivisionInfoByOrderKey] = useState<Record<string, DivisionInfo>>({});
  const [orderMetaEditor, setOrderMetaEditor] = useState<OrderMetaEditorState>({
    open: false,
    orderIndex: null,
    draftOrder: null,
    title: '',
  });
  const [splitEditor, setSplitEditor] = useState<SplitEditorState>({
    open: false,
    planId: null,
    sourceOrderKey: '',
    sourceShopId: '',
    targetShopId: '',
    percentage: 0,
    baseSkuTaglie: {},
    targetAllocation: {},
  });

  const buildSplitEditorRows = (baseSkuTaglie: Record<string, Record<string, number>>) => {
    const rows: Array<{ sku: string; taglia: string; baseQty: number; groupIndex: number }> = [];
    let groupIndex = -1;
    let prevSku = '';
    Object.entries(baseSkuTaglie).forEach(([sku, taglieMap]) => {
      if (sku !== prevSku) {
        groupIndex += 1;
        prevSku = sku;
      }
      Object.entries(taglieMap).forEach(([taglia, qty]) => {
        rows.push({
          sku,
          taglia,
          baseQty: Math.max(0, Math.round(Number(qty) || 0)),
          groupIndex,
        });
      });
    });
    return rows;
  };

  // Identita' dell'ordine: la sotto-consegna, non il codice consegna. Quest'ultimo lo
  // sceglie l'utente ed e' modificabile, quindi non puo' fare da chiave.
  const orderKey = (order: PreexecuteOrder) => `${order.shop_id}||${order.sotto_consegna}||${order.n_ordine}`;
  const normalizeShopId = (value: unknown): string => String(value ?? '').trim();
  const NON_EDITABLE_ORDER_KEYS = new Set(['righe']);
  const EDITOR_HIDDEN_KEYS = new Set(['righe', 'division_info', 'sotto_consegna']);

  const isEditableOrderFieldValue = (value: unknown): value is string | number | boolean => {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  };

  const parseAllNegoziFromPayload = (payload: unknown): { shopIds: string[]; shopNames: Record<string, string> } => {
    let normalizedPayload = payload;
    if (typeof normalizedPayload === 'string') {
      try {
        normalizedPayload = JSON.parse(normalizedPayload);
      } catch {
        normalizedPayload = {};
      }
    }
    const root = normalizedPayload && typeof normalizedPayload === 'object' ? (normalizedPayload as Record<string, unknown>) : {};
    const dataLayer =
      root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;

    const candidates: unknown[] = [
      dataLayer['admin-mappings-negozi'],
      dataLayer['negozi'],
      dataLayer,
      root['admin-mappings-negozi'],
      root['negozi'],
      root,
    ];

    const collectEntries = (container: unknown): Record<string, unknown>[] => {
      if (!container || typeof container !== 'object') return [];
      const rec = container as Record<string, unknown>;
      if (Array.isArray(rec.entries)) {
        return rec.entries.filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object' && !Array.isArray(x));
      }
      if (Array.isArray(container)) {
        return (container as unknown[]).filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object' && !Array.isArray(x));
      }
      return [];
    };

    const allEntries = candidates.flatMap((c) => collectEntries(c));
    const uniqueShopIds = Array.from(
      new Set(
        allEntries
          .map((entry) => normalizeShopId(entry.cod_negozio_ds))
          .filter((id) => id.length > 0),
      ),
    );

    const names: Record<string, string> = {};
    allEntries.forEach((entry) => {
      const id = normalizeShopId(entry.cod_negozio_ds);
      const name = normalizeShopId(entry.cod_shop_budget);
      if (id && name && !names[id]) {
        names[id] = name;
      }
    });

    return { shopIds: uniqueShopIds, shopNames: names };
  };

  const loadAllPossibleShops = async (
    optimizationShops: string[],
    currentShopMapping?: Record<string, string> | null,
  ) => {
    setAllPossibleShopIds(optimizationShops);
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'admin-get-mappings-negozi',
        params: {},
      });
      const parsed = parseAllNegoziFromPayload(result);
      const mergedShopIds = Array.from(new Set([...optimizationShops, ...parsed.shopIds]));
      setAllPossibleShopIds(mergedShopIds.length > 0 ? mergedShopIds : optimizationShops);
      if (Object.keys(parsed.shopNames).length > 0) {
        setShopMapping({
          ...(currentShopMapping ?? {}),
          ...parsed.shopNames,
        });
      }
    } catch {
      // Keep optimization shops as fallback
      setAllPossibleShopIds(optimizationShops);
    }
  };

  const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

  const isSplitNumericField = (key: string): boolean => {
    const normalized = key.toLowerCase();
    return /(qty|qta|quant|quantity|pezzi|pieces|totale|total)/.test(normalized);
  };

  const apportion = (total: number, percentages: number[]): number[] => {
    const safeTotal = Math.max(0, Math.round(total));
    if (safeTotal === 0) return percentages.map(() => 0);
    const raw = percentages.map((p) => (safeTotal * Math.max(0, p)) / 100);
    const floored = raw.map((v) => Math.floor(v));
    let remainder = safeTotal - floored.reduce((sum, v) => sum + v, 0);
    const order = raw
      .map((v, idx) => ({ idx, frac: v - floored[idx] }))
      .sort((a, b) => b.frac - a.frac);
    for (let i = 0; i < order.length && remainder > 0; i += 1) {
      floored[order[i].idx] += 1;
      remainder -= 1;
    }
    return floored;
  };

  const findTagliePath = (node: unknown, path: string[] = []): string[] | null => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
    const record = node as Record<string, unknown>;
    const entries = Object.entries(record);
    const allNumbers = entries.length > 0 && entries.every(([, v]) => typeof v === 'number');
    const allNestedNumberMaps =
      entries.length > 0 &&
      entries.every(([, v]) => {
        if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
        const nested = Object.values(v as Record<string, unknown>);
        return nested.length > 0 && nested.every((x) => typeof x === 'number');
      });
    const pathKey = path[path.length - 1]?.toLowerCase() ?? '';
    const looksLikeTaglie = /(taglie|taglia|size|sizes)/.test(pathKey);
    if (looksLikeTaglie && (allNumbers || allNestedNumberMaps)) return path;
    for (const [k, v] of entries) {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const found = findTagliePath(v, [...path, k]);
        if (found) return found;
      }
    }
    return null;
  };

  const getByPath = (obj: Record<string, unknown>, path: string[]): unknown =>
    path.reduce<unknown>((acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined), obj);

  const setByPath = (obj: Record<string, unknown>, path: string[], value: unknown): void => {
    if (path.length === 0) return;
    let cursor: Record<string, unknown> = obj;
    for (let i = 0; i < path.length - 1; i += 1) {
      const next = cursor[path[i]];
      if (!next || typeof next !== 'object' || Array.isArray(next)) {
        cursor[path[i]] = {};
      }
      cursor = cursor[path[i]] as Record<string, unknown>;
    }
    cursor[path[path.length - 1]] = value;
  };

  const toFlatTaglie = (value: unknown): Array<{ key: string; value: number }> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const record = value as Record<string, unknown>;
    const entries = Object.entries(record);
    const allNumbers = entries.length > 0 && entries.every(([, v]) => typeof v === 'number');
    if (allNumbers) {
      return entries.map(([k, v]) => ({ key: k, value: Number(v) || 0 }));
    }
    const result: Array<{ key: string; value: number }> = [];
    entries.forEach(([outer, nested]) => {
      if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return;
      Object.entries(nested as Record<string, unknown>).forEach(([inner, val]) => {
        if (typeof val === 'number') {
          result.push({ key: `${outer}::${inner}`, value: val });
        }
      });
    });
    return result;
  };

  const fromFlatTaglie = (flat: Array<{ key: string; value: number }>, original: unknown): unknown => {
    if (!original || typeof original !== 'object' || Array.isArray(original)) return original;
    const originalEntries = Object.entries(original as Record<string, unknown>);
    const allNumbers = originalEntries.length > 0 && originalEntries.every(([, v]) => typeof v === 'number');
    if (allNumbers) {
      return Object.fromEntries(flat.map(({ key, value }) => [key, Math.max(0, Math.round(value))]));
    }
    const grouped: Record<string, Record<string, number>> = {};
    flat.forEach(({ key, value }) => {
      const [outer, inner] = key.split('::');
      if (!outer || !inner) return;
      if (!grouped[outer]) grouped[outer] = {};
      grouped[outer][inner] = Math.max(0, Math.round(value));
    });
    return grouped;
  };

  const extractRigheRows = (order: PreexecuteOrder): Array<Record<string, unknown>> => {
    const root = order as Record<string, unknown>;
    const path = findRighePath(root, []);
    if (!path) return [];
    const rows = getByPath(root, path);
    if (!Array.isArray(rows)) return [];
    return rows.filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object' && !Array.isArray(row));
  };

  const lineKey = (row: Record<string, unknown>): string => {
    const sku = String(row.sku ?? '').trim();
    const taglia = String(row.taglia ?? '').trim();
    return `${sku}||${taglia}`;
  };

  const lineQty = (row: Record<string, unknown>): number => Math.max(0, Math.round(Number(row.capi ?? 0)));

  const summarizeOrderLineStats = (
    order: PreexecuteOrder,
    baselineRows?: Array<Record<string, unknown>>,
  ): OrderLineStats => {
    const rows = extractRigheRows(order);
    const currentMap = new Map<string, number>();
    rows.forEach((row) => currentMap.set(lineKey(row), lineQty(row)));

    const baselineMap = new Map<string, number>();
    (baselineRows ?? rows).forEach((row) => baselineMap.set(lineKey(row), lineQty(row)));

    const allKeys = new Set([...currentMap.keys(), ...baselineMap.keys()]);
    let linesChanged = 0;
    let linesActivated = 0;
    let linesDeactivated = 0;

    allKeys.forEach((key) => {
      const prevQty = baselineMap.get(key) ?? 0;
      const currQty = currentMap.get(key) ?? 0;
      if (prevQty !== currQty) linesChanged += 1;
      if (prevQty === 0 && currQty > 0) linesActivated += 1;
      if (prevQty > 0 && currQty === 0) linesDeactivated += 1;
    });

    const linesWithCapi = rows.filter((row) => lineQty(row) > 0).length;
    const totalCapi = rows.reduce((sum, row) => sum + lineQty(row), 0);

    return {
      linesTotal: rows.length,
      linesWithCapi,
      totalCapi,
      linesChanged,
      linesActivated,
      linesDeactivated,
    };
  };

  const mergeSkuTaglieKeys = (
    base: Record<string, Record<string, number>>,
    alloc: Record<string, Record<string, number>>,
  ): Record<string, Record<string, number>> => {
    const out: Record<string, Record<string, number>> = {};
    const skuKeys = Array.from(new Set([...Object.keys(base), ...Object.keys(alloc)]));
    skuKeys.forEach((sku) => {
      const taglieKeys = Array.from(
        new Set([
          ...Object.keys(base[sku] ?? {}),
          ...Object.keys(alloc[sku] ?? {}),
        ]),
      );
      out[sku] = {};
      taglieKeys.forEach((taglia) => {
        out[sku][taglia] = Math.max(0, Math.round(alloc[sku]?.[taglia] ?? 0));
      });
    });
    return out;
  };

  const sumSkuTaglie = (map: Record<string, Record<string, number>>): number =>
    Object.values(map).reduce(
      (acc, taglie) => acc + Object.values(taglie).reduce((s, v) => s + (Number(v) || 0), 0),
      0,
    );

  const computeDefaultTargetAllocation = (
    baseSkuTaglie: Record<string, Record<string, number>>,
    percentage: number,
  ): Record<string, Record<string, number>> => {
    const safePct = Math.max(0, Math.min(99, percentage));
    const target: Record<string, Record<string, number>> = {};

    const compareTagliaKey = (a: string, b: string) => {
      const aNum = Number(String(a).replace(',', '.'));
      const bNum = Number(String(b).replace(',', '.'));
      const aIsNum = Number.isFinite(aNum);
      const bIsNum = Number.isFinite(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      return String(a).localeCompare(String(b), 'it', { numeric: true });
    };

    Object.entries(baseSkuTaglie).forEach(([sku, taglieMap]) => {
      const total = Object.values(taglieMap).reduce((s, v) => s + (Number(v) || 0), 0);
      const targetQty = Math.round((total * safePct) / 100);
      const sortedTaglie = Object.entries(taglieMap).sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return compareTagliaKey(a[0], b[0]);
      });

      let remaining = targetQty;
      const alloc: Record<string, number> = {};
      sortedTaglie.forEach(([taglia, qty]) => {
        const take = Math.min(qty, remaining);
        alloc[taglia] = Math.max(0, take);
        remaining -= take;
      });
      target[sku] = alloc;
    });

    return target;
  };

  const parseSkuIdFromItem = (item: Record<string, unknown>): string | null => {
    const candidateKeys = ['sku', 'cod_sku', 'sku_id', 'modello', 'model', 'item_code', 'code'];
    for (const key of candidateKeys) {
      const val = item[key];
      const text = String(val ?? '').trim();
      if (text.length > 0) return text;
    }
    return null;
  };

  const findRighePath = (node: unknown, path: string[] = []): string[] | null => {
    if (!node || typeof node !== 'object') return null;
    const record = node as Record<string, unknown>;
    for (const [k, v] of Object.entries(record)) {
      if (Array.isArray(v) && v.length > 0) {
        const hasRigheRows = v.some((row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
          const r = row as Record<string, unknown>;
          return (
            typeof r.sku !== 'undefined' &&
            typeof r.taglia !== 'undefined' &&
            typeof r.capi !== 'undefined'
          );
        });
        if (hasRigheRows) return [...path, k];
      }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const found = findRighePath(v, [...path, k]);
        if (found) return found;
      }
    }
    return null;
  };

  const findSkuArrayPath = (
    node: unknown,
    path: string[] = [],
  ): { path: string[]; itemTagliePaths: Record<string, string[]> } | null => {
    if (!node || typeof node !== 'object') return null;
    const record = node as Record<string, unknown>;

    for (const [k, v] of Object.entries(record)) {
      if (Array.isArray(v) && v.length > 0) {
        const itemTagliePaths: Record<string, string[]> = {};
        for (const rawItem of v) {
          if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) continue;
          const item = rawItem as Record<string, unknown>;
          const sku = parseSkuIdFromItem(item);
          const tagliePath = findTagliePath(item, []);
          if (sku && tagliePath) {
            itemTagliePaths[sku] = tagliePath;
          }
        }
        if (Object.keys(itemTagliePaths).length > 0) {
          return { path: [...path, k], itemTagliePaths };
        }
      }
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const found = findSkuArrayPath(v, [...path, k]);
        if (found) return found;
      }
    }
    return null;
  };

  const findSkuObjectMapPath = (
    node: unknown,
    path: string[] = [],
  ): { path: string[]; skuTagliePaths: Record<string, string[]> } | null => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
    const record = node as Record<string, unknown>;
    const entries = Object.entries(record);
    const skuTagliePaths: Record<string, string[]> = {};
    entries.forEach(([key, val]) => {
      if (!val || typeof val !== 'object' || Array.isArray(val)) return;
      const tagliePath = findTagliePath(val, []);
      if (tagliePath) {
        skuTagliePaths[key] = tagliePath;
      }
    });
    if (Object.keys(skuTagliePaths).length >= 2) {
      return { path, skuTagliePaths };
    }
    for (const [k, v] of entries) {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const found = findSkuObjectMapPath(v, [...path, k]);
        if (found) return found;
      }
    }
    return null;
  };

  const extractSkuTaglieAdapter = (order: PreexecuteOrder): SkuTaglieAdapter | null => {
    const sourceOrder = order as Record<string, unknown>;

    // Primary path for weborder export payload:
    // order.righe = [{ sku, taglia, capi, ... }, ...]
    const righePath = findRighePath(sourceOrder, []);
    if (righePath) {
      const rows = getByPath(sourceOrder, righePath);
      if (Array.isArray(rows)) {
        const skuTaglie: Record<string, Record<string, number>> = {};
        rows.forEach((rawRow) => {
          if (!rawRow || typeof rawRow !== 'object' || Array.isArray(rawRow)) return;
          const row = rawRow as Record<string, unknown>;
          const sku = String(row.sku ?? '').trim();
          const taglia = String(row.taglia ?? '').trim();
          const capi = Number(row.capi ?? 0);
          if (!sku || !taglia) return;
          if (!skuTaglie[sku]) skuTaglie[sku] = {};
          skuTaglie[sku][taglia] = (skuTaglie[sku][taglia] ?? 0) + Math.max(0, Math.round(capi));
        });

        if (Object.keys(skuTaglie).length > 0) {
          return {
            skuTaglie,
            write: (targetOrder, nextSkuTaglie) => {
              const targetRows = getByPath(targetOrder, righePath);
              if (!Array.isArray(targetRows)) return;
              targetRows.forEach((rawRow) => {
                if (!rawRow || typeof rawRow !== 'object' || Array.isArray(rawRow)) return;
                const row = rawRow as Record<string, unknown>;
                const sku = String(row.sku ?? '').trim();
                const taglia = String(row.taglia ?? '').trim();
                if (!sku || !taglia) return;
                const nextQty = Math.max(0, Math.round(nextSkuTaglie[sku]?.[taglia] ?? 0));
                row.capi = nextQty;
              });
            },
          };
        }
      }
    }

    const arrayPattern = findSkuArrayPath(sourceOrder, []);
    if (arrayPattern) {
      const arr = getByPath(sourceOrder, arrayPattern.path);
      if (Array.isArray(arr)) {
        const skuTaglie: Record<string, Record<string, number>> = {};
        arr.forEach((rawItem) => {
          if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) return;
          const item = rawItem as Record<string, unknown>;
          const sku = parseSkuIdFromItem(item);
          if (!sku) return;
          const itemPath = arrayPattern.itemTagliePaths[sku] ?? findTagliePath(item, []);
          if (!itemPath) return;
          const taglieNode = getByPath(item, itemPath);
          const flat = toFlatTaglie(taglieNode);
          if (flat.length > 0) {
            skuTaglie[sku] = Object.fromEntries(
              flat.map((entry) => [entry.key, Math.max(0, Math.round(entry.value))]),
            );
          }
        });
        if (Object.keys(skuTaglie).length > 0) {
          return {
            skuTaglie,
            write: (targetOrder, nextSkuTaglie) => {
              const targetArray = getByPath(targetOrder, arrayPattern.path);
              if (!Array.isArray(targetArray)) return;
              targetArray.forEach((rawItem) => {
                if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) return;
                const item = rawItem as Record<string, unknown>;
                const sku = parseSkuIdFromItem(item);
                if (!sku || !nextSkuTaglie[sku]) return;
                const itemPath = arrayPattern.itemTagliePaths[sku] ?? findTagliePath(item, []);
                if (!itemPath) return;
                const current = getByPath(item, itemPath);
                const rebuilt = fromFlatTaglie(
                  Object.entries(nextSkuTaglie[sku]).map(([key, value]) => ({ key, value })),
                  current,
                );
                setByPath(item, itemPath, rebuilt);
              });
            },
          };
        }
      }
    }

    const mapPattern = findSkuObjectMapPath(sourceOrder, []);
    if (mapPattern) {
      const root = getByPath(sourceOrder, mapPattern.path);
      if (root && typeof root === 'object' && !Array.isArray(root)) {
        const rootRecord = root as Record<string, unknown>;
        const skuTaglie: Record<string, Record<string, number>> = {};
        Object.entries(mapPattern.skuTagliePaths).forEach(([sku, skuPath]) => {
          const node = rootRecord[sku];
          if (!node || typeof node !== 'object' || Array.isArray(node)) return;
          const taglieNode = getByPath(node as Record<string, unknown>, skuPath);
          const flat = toFlatTaglie(taglieNode);
          if (flat.length > 0) {
            skuTaglie[sku] = Object.fromEntries(
              flat.map((entry) => [entry.key, Math.max(0, Math.round(entry.value))]),
            );
          }
        });
        if (Object.keys(skuTaglie).length > 0) {
          return {
            skuTaglie,
            write: (targetOrder, nextSkuTaglie) => {
              const targetRoot = getByPath(targetOrder, mapPattern.path);
              if (!targetRoot || typeof targetRoot !== 'object' || Array.isArray(targetRoot)) return;
              const targetRootRecord = targetRoot as Record<string, unknown>;
              Object.entries(mapPattern.skuTagliePaths).forEach(([sku, skuPath]) => {
                if (!nextSkuTaglie[sku]) return;
                const skuNode = targetRootRecord[sku];
                if (!skuNode || typeof skuNode !== 'object' || Array.isArray(skuNode)) return;
                const current = getByPath(skuNode as Record<string, unknown>, skuPath);
                const rebuilt = fromFlatTaglie(
                  Object.entries(nextSkuTaglie[sku]).map(([key, value]) => ({ key, value })),
                  current,
                );
                setByPath(skuNode as Record<string, unknown>, skuPath, rebuilt);
              });
            },
          };
        }
      }
    }

    return null;
  };

  const buildSourceFromTargetAllocation = (
    base: Record<string, Record<string, number>>,
    target: Record<string, Record<string, number>>,
  ): Record<string, Record<string, number>> => {
    const source: Record<string, Record<string, number>> = {};
    const skuKeys = Array.from(new Set([...Object.keys(base), ...Object.keys(target)]));
    skuKeys.forEach((sku) => {
      const tagliaKeys = Array.from(
        new Set([...Object.keys(base[sku] ?? {}), ...Object.keys(target[sku] ?? {})]),
      );
      source[sku] = {};
      tagliaKeys.forEach((taglia) => {
        const baseQty = Math.max(0, Math.round(base[sku]?.[taglia] ?? 0));
        const targetQty = Math.max(0, Math.round(target[sku]?.[taglia] ?? 0));
        source[sku][taglia] = Math.max(0, baseQty - targetQty);
      });
    });
    return source;
  };

  const tryParseJson = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  const isOrderLike = (value: unknown): value is PreexecuteOrder => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const record = value as Record<string, unknown>;
    const hasRighe = Array.isArray(record.righe);
    const hasOrderIdentity =
      typeof record.n_ordine !== 'undefined' ||
      typeof record.shop_id !== 'undefined' ||
      typeof record.sotto_consegna !== 'undefined' ||
      typeof record.consegna !== 'undefined';
    return hasRighe || hasOrderIdentity;
  };

  const normalizePreexecuteOrders = (raw: unknown): PreexecuteOrder[] => {
    let payload = tryParseJson(raw);

    // Unwrap nested payloads like { data: "..." }, { outputs: [...] }, etc.
    for (let i = 0; i < 4; i += 1) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) break;
      const record = payload as Record<string, unknown>;
      if (Array.isArray(record.outputs)) {
        payload = record.outputs;
        continue;
      }
      if (Array.isArray(record.orders)) {
        payload = record.orders;
        continue;
      }
      if (typeof record.data !== 'undefined') {
        payload = tryParseJson(record.data);
        continue;
      }
      break;
    }

    if (Array.isArray(payload)) {
      return payload.filter(isOrderLike).map((order) => ({ ...order }));
    }

    if (isOrderLike(payload)) {
      return [{ ...payload }];
    }

    // Legacy grouped shape: { shop_id: [orders...] }
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const grouped = payload as Record<string, unknown>;
      const orders: PreexecuteOrder[] = [];
      Object.entries(grouped).forEach(([shopId, maybeOrders]) => {
        if (!Array.isArray(maybeOrders)) return;
        maybeOrders.forEach((entry) => {
          if (!isOrderLike(entry)) return;
          const order = { ...(entry as Record<string, unknown>) } as PreexecuteOrder;
          if (!order.shop_id) order.shop_id = shopId;
          orders.push(order);
        });
      });
      return orders;
    }

    return [];
  };

  // Load schema when modal opens
  useEffect(() => {
    if (isOpen && !schema) {
      void detectAggregatedShops();
      loadSchema();
    }
  }, [isOpen]);

  // SPJ-892: rileva i negozi aggregati dal risultato grezzo (come l'export Excel), così
  // possiamo offrire i raggruppamenti prima che i tab vengano splittati dal backend.
  const detectAggregatedShops = async () => {
    try {
      const optimizationResult = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-optimization-result',
        params: { 'optim-run-id': optimizationId },
      }) as any;
      const mapping = (optimizationResult?.shop_mapping && typeof optimizationResult.shop_mapping === 'object')
        ? optimizationResult.shop_mapping as Record<string, string>
        : {};
      const cleanShopId = (raw: string) => String(raw || '').replace(/\|/g, '').trim();
      const isArea = (id: string) => {
        const c = cleanShopId(id).toLowerCase();
        return c === 'area' || String(mapping[cleanShopId(id)] || '').trim().toLowerCase() === 'area';
      };
      const source = (optimizationResult?.metrics_per_consegna_per_shop && typeof optimizationResult.metrics_per_consegna_per_shop === 'object')
        ? optimizationResult.metrics_per_consegna_per_shop as Record<string, unknown>
        : {};
      const aggregated = Object.keys(source)
        .filter((key) => key.includes('|,|') && !isArea(key))
        .map((sourceId) => ({
          sourceId,
          members: sourceId.split('|,|').map(cleanShopId)
            .filter((id) => id.length > 0 && !isArea(id))
            .map((id) => ({ id, label: mapping[id] || id })),
        }))
        .filter((entry) => entry.members.length > 1);
      setAggregatedShops(aggregated);
    } catch (err) {
      console.warn('Weborder: rilevamento negozi aggregati non riuscito', err);
    }
  };

  // SPJ-892: applica i raggruppamenti scelti e ricarica i tab già splittati per gruppo.
  const applyShopGroups = async () => {
    setAppliedShopGroups(shopGroups);
    setSchema(null);
    await loadSchema(shopGroups);
  };

  const loadSchema = async (groups?: Record<string, string>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-weborder-export-modal',
        params: {
          'optim-run-id': optimizationId,
          'shop-groups': groups ?? {},
        },
      }) as any;

      if (result.status === 'error') {
        setError(result.errstr || 'Impossibile caricare lo schema di esportazione');
        return;
      }

      const schemaData = result.data && typeof result.data === 'object' && !Array.isArray(result.data)
        ? result.data as OrderSchema
        : {};
      const collectionMap = result.collection_mapping && typeof result.collection_mapping === 'object' && !Array.isArray(result.collection_mapping)
        ? result.collection_mapping as CollectionMapping
        : {};
      const shops = Array.isArray(result.shop_ids)
        ? result.shop_ids.filter((shopId: unknown): shopId is string => typeof shopId === 'string' && shopId.trim().length > 0)
        : Object.keys(schemaData || {});
      const shopMap = result.shop_mapping && typeof result.shop_mapping === 'object' && !Array.isArray(result.shop_mapping)
        ? result.shop_mapping as Record<string, string>
        : undefined;

      if (shops.length === 0) {
        setError('Nessuno shop disponibile per esportare il Weborder. Verifica che la run selezionata contenga righe esportabili.');
        setSchema(schemaData);
        setCollectionMapping(collectionMap);
        setShopIds([]);
        setAllPossibleShopIds([]);
        setShopMapping(shopMap ?? null);
        return;
      }
      
      setSchema(schemaData);
      setCollectionMapping(collectionMap);
      setShopIds(shops);
      setAllPossibleShopIds(shops);
      setShopMapping(shopMap ?? null);
      void loadAllPossibleShops(shops, shopMap ?? null);

      // Initialize values with defaults - now triply nested
      const initialValues: ValuesState = {};
      
      // Set active tabs to first shop and first sotto_consegna of that shop
      if (shops.length > 0) {
        const firstShop = shops[0];
        setActiveShopTab(firstShop);
        
        const sottoConsegnas = Object.keys(schemaData[firstShop] || {});
        if (sottoConsegnas.length > 0) {
          setActiveSottoConsegnaTab(sottoConsegnas[0]);
        }
      }

      // Initialize values for all shops and sotto_consegnas
      shops.forEach(shopId => {
        initialValues[shopId] = {};
        const shopSchema = schemaData[shopId] || {};
        
        Object.keys(shopSchema).forEach(sottoConsegna => {
          initialValues[shopId][sottoConsegna] = {};
          const fields = Array.isArray(shopSchema[sottoConsegna]) ? shopSchema[sottoConsegna] : [];
          fields.forEach(field => {
            initialValues[shopId][sottoConsegna][field.name] = {
              value: field.default ?? '',
              validated: !field.must_validate, // Auto-validate if not required
              modified: false,
            };
          });
        });
      });

      setValues(initialValues);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  // Check if a field value is valid based on its type
  const isFieldValueValid = (value: string | number, fieldType: 'date' | 'str' | 'int'): boolean => {
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    
    if (fieldType === 'date') {
      // Check if it's a valid date in YYYY-MM-DD format
      const dateStr = String(value);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return false;
      }
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    }
    
    if (fieldType === 'int') {
      return typeof value === 'number' && !isNaN(value);
    }
    
    // For strings, just check it's not empty
    return String(value).trim().length > 0;
  };

  // SPJ-1363: campi per-negozio che non cambiano mai tra le sotto-consegne di uno stesso
  // negozio (cliente/recapito/causale): appena scritti su una sotto-consegna si copiano
  // automaticamente su tutte le altre del negozio.
  const PER_SHOP_COPY_FIELDS = ['cliente', 'recapito', 'causale_trasporto'];

  // SPJ-1363: chiave per lo stato loading/errore della GET date consegna.
  const deliveryKey = (shopId: string, sottoConsegna: string) => `${shopId}::${sottoConsegna}`;

  const handleFieldChange = (shopId: string, sottoConsegna: string, fieldName: string, newValue: string | number, fieldType: 'date' | 'str' | 'int') => {
    const valueIsValid = isFieldValueValid(newValue, fieldType);
    // SPJ-1363: il codice consegna NON si auto-valida anche quando il valore e' valido:
    // deve restare non validato finche' l'utente non clicca "Valida", che e' cio' che
    // scatena la chiamata BASP (optim-get-delivery-info) per precompilare date+descrizione.
    const autoValidate = fieldName === 'consegna' ? false : valueIsValid;
    // SPJ-1363: cliente/recapito/causale si propagano subito alle altre sotto-consegne.
    const propagatePerShop = PER_SHOP_COPY_FIELDS.includes(fieldName);

    // SPJ-1363: riscrivere il codice consegna azzera un eventuale errore precedente.
    if (fieldName === 'consegna') {
      const key = deliveryKey(shopId, sottoConsegna);
      setDeliveryError(prev => {
        if (!(key in prev)) return prev;
        const n = { ...prev }; delete n[key]; return n;
      });
    }

    setValues(prev => {
      const nextSotto = {
        ...prev[shopId]?.[sottoConsegna],
        [fieldName]: {
          ...prev[shopId]?.[sottoConsegna]?.[fieldName],
          value: newValue,
          validated: autoValidate, // Only auto-validate if valid (mai per 'consegna')
          modified: true, // Mark as modified by user
        },
      };
      const nextShop = { ...prev[shopId], [sottoConsegna]: nextSotto };

      if (propagatePerShop && schema?.[shopId]) {
        Object.keys(schema[shopId]).forEach(sc => {
          if (sc === sottoConsegna) return;
          const hasField = (schema[shopId][sc] || []).some(fld => fld.name === fieldName);
          if (!hasField) return;
          nextShop[sc] = {
            ...prev[shopId]?.[sc],
            [fieldName]: { ...(prev[shopId]?.[sc]?.[fieldName] || {}), value: newValue, validated: valueIsValid, modified: true },
          };
        });
      }

      return { ...prev, [shopId]: nextShop };
    });
  };

  // SPJ-1363: quando l'utente compila il codice consegna, precompila date + descrizione
  // dalla BASP tramite l'azione persona (autenticata col token dell'utente lato server).
  const precompileDeliveryDates = async (shopId: string, sottoConsegna: string) => {
    const key = deliveryKey(shopId, sottoConsegna);
    const fields = values[shopId]?.[sottoConsegna] || {};
    const season = String(fields['stagione']?.value ?? '').trim();
    const linea = String(fields['linea_vendita']?.value ?? '').trim();
    const delivery = String(fields['consegna']?.value ?? '').trim();
    if (!season || !linea || !delivery) return;
    // SPJ-1363: mostra lo spinner e azzera un eventuale errore precedente durante la GET.
    setDeliveryLoading(prev => ({ ...prev, [key]: true }));
    setDeliveryError(prev => {
      if (!(key in prev)) return prev;
      const n = { ...prev }; delete n[key]; return n;
    });
    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-delivery-info',
        params: { season, linea, delivery },
      });
      if (extractWeborderError(result)) {
        console.warn('optim-get-delivery-info error', result);
        setDeliveryError(prev => ({ ...prev, [key]: 'Errore nel recupero della consegna' }));
        return;
      }
      const info: any = normalizeActionPayload(result) || {};
      const data: any = info.data ?? info;
      const patch: Record<string, string> = {};
      if (data.starting_date) patch['data_inizio_consegna'] = String(data.starting_date);
      if (data.ending_date) patch['data_fine_consegna'] = String(data.ending_date);
      if (typeof data.note === 'string' && data.note.trim()) patch['note_consegna'] = data.note.trim();
      // SPJ-1363: la BASP non conosce questo codice consegna (risposta vuota): niente
      // validazione, si mostra l'errore in rosso al posto di "Validato".
      if (Object.keys(patch).length === 0) {
        setDeliveryError(prev => ({ ...prev, [key]: `Codice consegna "${delivery}" non trovato` }));
        return;
      }
      // SPJ-1363: successo -> il codice consegna e' valido. season/linea/delivery sono
      // identici per tutti i negozi della run, quindi codice consegna + date + descrizione
      // valgono per ogni negozio che ha questa stessa sotto-consegna: li propaghiamo a tutti.
      patch['consegna'] = delivery;
      setValues(prev => {
        let next = { ...prev };
        const targetShops = Object.keys(schema || {}).filter(sid => !!schema?.[sid]?.[sottoConsegna]);
        (targetShops.length ? targetShops : [shopId]).forEach(sid => {
          const scPrev = next[sid]?.[sottoConsegna] || {};
          const scNext: any = { ...scPrev };
          Object.entries(patch).forEach(([fname, val]) => {
            const hasField = (schema?.[sid]?.[sottoConsegna] || []).some(fld => fld.name === fname);
            if (!hasField) return;
            scNext[fname] = { ...(scPrev[fname] || {}), value: val, validated: true, modified: true };
          });
          next = { ...next, [sid]: { ...next[sid], [sottoConsegna]: scNext } };
        });
        return next;
      });
    } catch (e) {
      console.warn('precompileDeliveryDates failed', e);
      setDeliveryError(prev => ({ ...prev, [key]: 'Errore di rete nel recupero della consegna' }));
    } finally {
      setDeliveryLoading(prev => {
        if (!(key in prev)) return prev;
        const n = { ...prev }; delete n[key]; return n;
      });
    }
  };

  const handleValidate = (shopId: string, sottoConsegna: string, fieldName: string) => {
    // SPJ-1363: per il codice consegna la validazione NON e' immediata: dipende dalla
    // BASP. precompileDeliveryDates valida (e propaga codice+date+descrizione a tutti i
    // negozi con la stessa sotto-consegna) se trova la consegna, altrimenti mostra errore.
    if (fieldName === 'consegna') {
      void precompileDeliveryDates(shopId, sottoConsegna);
      return;
    }

    // SPJ-1363: cliente/recapito/causale non cambiano tra le sotto-consegne del negozio:
    // validando (o accettando il default) su una, si valida e si copia direttamente anche
    // sulle altre — cosi' non serve ripetere la validazione consegna per consegna.
    const propagatePerShop = PER_SHOP_COPY_FIELDS.includes(fieldName);

    setValues(prev => {
      const validatedValue = prev[shopId]?.[sottoConsegna]?.[fieldName]?.value;
      const nextSotto = {
        ...prev[shopId]?.[sottoConsegna],
        [fieldName]: {
          ...prev[shopId]?.[sottoConsegna]?.[fieldName],
          validated: true,
        },
      };
      let next = { ...prev, [shopId]: { ...prev[shopId], [sottoConsegna]: nextSotto } };

      if (propagatePerShop && schema?.[shopId]) {
        const nextShop = { ...next[shopId] };
        Object.keys(schema[shopId]).forEach(sc => {
          if (sc === sottoConsegna) return;
          const hasField = (schema[shopId][sc] || []).some(fld => fld.name === fieldName);
          if (!hasField) return;
          nextShop[sc] = {
            ...next[shopId]?.[sc],
            [fieldName]: { ...(next[shopId]?.[sc]?.[fieldName] || {}), value: validatedValue, validated: true, modified: true },
          };
        });
        next = { ...next, [shopId]: nextShop };
      }

      return next;
    });
  };

  const isSottoConsegnaValidated = (shopId: string, sottoConsegna: string): boolean => {
    if (!schema || !schema[shopId]?.[sottoConsegna]) return true;

    for (const field of schema[shopId][sottoConsegna]) {
      if (field.must_validate) {
        const fieldValue = values[shopId]?.[sottoConsegna]?.[field.name];
        if (!fieldValue?.validated) {
          return false;
        }
      }
    }
    return true;
  };

  const isShopValidated = (shopId: string): boolean => {
    if (!schema || !schema[shopId]) return true;

    for (const sottoConsegna of Object.keys(schema[shopId])) {
      if (!isSottoConsegnaValidated(shopId, sottoConsegna)) {
        return false;
      }
    }
    return true;
  };

  const isAllValidated = (): boolean => {
    if (!schema) return false;

    for (const shopId of shopIds) {
      if (!isShopValidated(shopId)) {
        return false;
      }
    }
    return true;
  };

  const handlePreexecute = async () => {
    if (!isAllValidated()) {
      alert('Valida tutti i campi obbligatori prima di procedere');
      return;
    }

    setExecuting(true);
    setPreexecuteError(null);
    setPreexecuteOrders([]);

    try {
      // Prepare validated data - now nested by shop_id then sotto_consegna
      const validatedData: Record<string, Record<string, Record<string, string | number>>> = {};
      
      Object.keys(values).forEach(shopId => {
        validatedData[shopId] = {};
        Object.keys(values[shopId]).forEach(sottoConsegna => {
          validatedData[shopId][sottoConsegna] = {};
          Object.keys(values[shopId][sottoConsegna]).forEach(fieldName => {
            validatedData[shopId][sottoConsegna][fieldName] = values[shopId][sottoConsegna][fieldName].value;
          });
        });
      });

      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-export-weborder-preexecute',
        params: {
          'optim-validated-data': validatedData,
          // SPJ-892: stessi raggruppamenti del modal, così lo split degli ordini usa le
          // stesse chiavi negozio dei tab (altrimenti mismatch e ordini vuoti).
          'shop-groups': appliedShopGroups,
        },
      }) as any;

      const preexecuteErrorMessage = extractWeborderError(result);
      if (preexecuteErrorMessage) {
        setPreexecuteError(preexecuteErrorMessage || 'Pre-esecuzione fallita');
        return;
      }

      const resultPayload = normalizeActionPayload(result);
      const normalizedOrders = normalizePreexecuteOrders(resultPayload?.data);
      const allOrders: OrderExecutionState[] = normalizedOrders.map((order) => ({
        order,
        status: 'pending',
      }));

      if (allOrders.length === 0) {
        setPreexecuteError(
          `Nessun ordine generato o formato non riconosciuto.\nPayload ricevuto: ${String(result?.data).slice(0, 300)}`
        );
        return;
      }

      setBasePreexecuteOrders(allOrders);
      setPreexecuteOrders(allOrders);
      setSplitPlans([]);
      setSplitError(null);
      setDivisionInfoByOrderKey({});
      setOrderLineStatsByOrderKey(
        Object.fromEntries(
          allOrders.map((entry) => [orderKey(entry.order), summarizeOrderLineStats(entry.order)]),
        ),
      );
    } catch (err) {
      setPreexecuteError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setExecuting(false);
    }
  };

  const addSplitPlan = () => {
    if (basePreexecuteOrders.length === 0) return;
    const sourceOrder = basePreexecuteOrders[0].order;
    const defaultTarget = allPossibleShopIds.find((id) => id !== sourceOrder.shop_id) ?? sourceOrder.shop_id;
    setSplitPlans((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        sourceOrderKey: orderKey(sourceOrder),
        targetShopId: defaultTarget,
        percentage: 10,
      },
    ]);
  };

  const openSplitEditor = (planId: string) => {
    const plan = splitPlans.find((p) => p.id === planId);
    if (!plan) return;
    const source = basePreexecuteOrders.find((o) => orderKey(o.order) === plan.sourceOrderKey)?.order;
    if (!source) return;

    const adapter = extractSkuTaglieAdapter(source);
    if (!adapter) {
      const keys = Object.keys((source as unknown as Record<string, unknown>) ?? {}).join(', ');
      alert(`Struttura SKU/Taglie non riconosciuta per questo ordine.\nChiavi top-level: ${keys || '(nessuna)'}\nAtteso almeno: righe[] con campi sku/taglia/capi.`);
      return;
    }

    const mergedManual = plan.manualTargetAllocation
      ? mergeSkuTaglieKeys(adapter.skuTaglie, plan.manualTargetAllocation)
      : undefined;
    const defaultAllocation = computeDefaultTargetAllocation(adapter.skuTaglie, plan.percentage);
    const targetAllocation = mergedManual ?? defaultAllocation;

    setSplitEditor({
      open: true,
      planId,
      sourceOrderKey: plan.sourceOrderKey,
      sourceShopId: source.shop_id,
      targetShopId: plan.targetShopId,
      percentage: plan.percentage,
      baseSkuTaglie: adapter.skuTaglie,
      targetAllocation,
    });
  };

  const saveSplitEditor = () => {
    if (!splitEditor.planId) return;
    setSplitPlans((prev) =>
      prev.map((plan) =>
        plan.id === splitEditor.planId
          ? {
              ...plan,
              manualTargetAllocation: mergeSkuTaglieKeys(splitEditor.baseSkuTaglie, splitEditor.targetAllocation),
            }
          : plan,
      ),
    );
    setSplitEditor((prev) => ({ ...prev, open: false }));
  };

  const moveTagliaOneUnit = (sku: string, taglia: string, direction: 'source-to-target' | 'target-to-source') => {
    setSplitEditor((prev) => {
      const baseQty = Math.max(0, Math.round(prev.baseSkuTaglie[sku]?.[taglia] ?? 0));
      const currentTarget = Math.max(0, Math.round(prev.targetAllocation[sku]?.[taglia] ?? 0));
      const nextTarget =
        direction === 'source-to-target'
          ? Math.min(baseQty, currentTarget + 1)
          : Math.max(0, currentTarget - 1);

      return {
        ...prev,
        targetAllocation: {
          ...prev.targetAllocation,
          [sku]: {
            ...(prev.targetAllocation[sku] ?? {}),
            [taglia]: nextTarget,
          },
        },
      };
    });
  };

  const applySplitPlans = () => {
    if (basePreexecuteOrders.length === 0 || splitPlans.length === 0) return;
    setSplitError(null);

    const planBySource = new Map<string, SplitPlan[]>();
    splitPlans.forEach((plan) => {
      if (!planBySource.has(plan.sourceOrderKey)) planBySource.set(plan.sourceOrderKey, []);
      planBySource.get(plan.sourceOrderKey)?.push(plan);
    });

    for (const [sourceKey, plans] of planBySource.entries()) {
      const sourceOrder = basePreexecuteOrders.find((o) => orderKey(o.order) === sourceKey)?.order;
      if (!sourceOrder) continue;
      const totalPct = plans.reduce((sum, p) => sum + Math.max(0, p.percentage), 0);
      if (totalPct >= 100) {
        setSplitError(`Percentuale totale >= 100% per ordine ${sourceOrder.n_ordine}.`);
        return;
      }
      const invalidTarget = plans.some((p) => p.targetShopId === sourceOrder.shop_id);
      if (invalidTarget) {
        setSplitError(`Target shop non valido (uguale alla sorgente) per ordine ${sourceOrder.n_ordine}.`);
        return;
      }
    }

    const nextOrders: OrderExecutionState[] = [];
    const nextLineStats: Record<string, OrderLineStats> = {};
    const nextDivisionInfo: Record<string, DivisionInfo> = {};

    basePreexecuteOrders.forEach((state) => {
      const sourceKey = orderKey(state.order);
      const plans = planBySource.get(sourceKey) ?? [];
      if (plans.length === 0) {
        nextOrders.push({ ...state, status: 'pending', error: undefined, result: undefined });
        nextLineStats[sourceKey] = summarizeOrderLineStats(state.order);
        return;
      }

      const sourceOrder = deepClone(state.order) as Record<string, unknown>;
      const sourcePlans = plans.map((p) => ({ ...p, pct: Math.max(0, p.percentage) }));
      const targetOrders = sourcePlans.map((plan, idx) => {
        const cloned = deepClone(state.order) as Record<string, unknown>;
        cloned.shop_id = plan.targetShopId;
        const baseOrderNo = String(cloned.n_ordine ?? state.order.n_ordine);
        cloned.n_ordine = `${baseOrderNo}-T${idx + 1}`;
        return { plan, order: cloned };
      });

      const skuAdapter = extractSkuTaglieAdapter(state.order);
      if (skuAdapter) {
        let runningSourceSkuTaglie = deepClone(skuAdapter.skuTaglie);
        const sourceBaselineRows = extractRigheRows(state.order);
        const sourceOrderMut = deepClone(state.order) as Record<string, unknown>;
        const targetOrdersMut = targetOrders.map((t) => deepClone(t.order) as Record<string, unknown>);

        targetOrders.forEach((targetOrder, idx) => {
          const manual = targetOrder.plan.manualTargetAllocation;
          const defaultAlloc = computeDefaultTargetAllocation(runningSourceSkuTaglie, targetOrder.plan.pct);
          const effectiveTarget = manual
            ? mergeSkuTaglieKeys(runningSourceSkuTaglie, manual)
            : defaultAlloc;

          const normalizedTarget: Record<string, Record<string, number>> = {};
          Object.keys(runningSourceSkuTaglie).forEach((sku) => {
            normalizedTarget[sku] = {};
            Object.keys(runningSourceSkuTaglie[sku] ?? {}).forEach((taglia) => {
              const sourceQty = Math.max(0, Math.round(runningSourceSkuTaglie[sku]?.[taglia] ?? 0));
              const desired = Math.max(0, Math.round(effectiveTarget[sku]?.[taglia] ?? 0));
              normalizedTarget[sku][taglia] = Math.min(sourceQty, desired);
            });
          });

          const nextSource = buildSourceFromTargetAllocation(runningSourceSkuTaglie, normalizedTarget);
          skuAdapter.write(targetOrdersMut[idx], normalizedTarget);
          runningSourceSkuTaglie = nextSource;
        });

        skuAdapter.write(sourceOrderMut, runningSourceSkuTaglie);

        const sourceOrderTyped = sourceOrderMut as PreexecuteOrder;
        const firstTarget = targetOrders[0];
        if (firstTarget) {
          (sourceOrderTyped as Record<string, unknown>).division_info = {
            role: 'source',
            source_shop_id: state.order.shop_id,
            targets: targetOrders.map((target) => ({
              target_shop_id: target.plan.targetShopId,
              percentage: target.plan.pct,
            })),
          };
        }
        nextOrders.push({
          order: sourceOrderTyped,
          status: 'pending',
        });
        const sourceOrderId = orderKey(sourceOrderTyped);
        nextLineStats[sourceOrderId] = summarizeOrderLineStats(sourceOrderTyped, sourceBaselineRows);
        targetOrdersMut.forEach((order, idx) => {
          const targetTyped = order as PreexecuteOrder;
          (targetTyped as Record<string, unknown>).division_info = {
            role: 'target',
            source_shop_id: state.order.shop_id,
            target_shop_id: targetOrders[idx]?.plan.targetShopId ?? targetTyped.shop_id,
            percentage: targetOrders[idx]?.plan.pct ?? 0,
          };
          nextOrders.push({
            order: targetTyped,
            status: 'pending',
          });
          const targetOrderId = orderKey(targetTyped);
          nextLineStats[targetOrderId] = summarizeOrderLineStats(targetTyped, []);
          nextDivisionInfo[targetOrderId] = {
            sourceShopId: state.order.shop_id,
            targetShopId: targetOrders[idx]?.plan.targetShopId ?? targetTyped.shop_id,
            percentage: targetOrders[idx]?.plan.pct ?? 0,
            role: 'target',
          };
        });
        if (targetOrders.length > 0) {
          nextDivisionInfo[sourceOrderId] = {
            sourceShopId: state.order.shop_id,
            targetShopId: targetOrders[0].plan.targetShopId,
            percentage: targetOrders[0].plan.pct,
            role: 'source',
          };
        }
        return;
      }

      const walk = (sourceNode: unknown, targetNodes: unknown[], parentKey = ''): { source: unknown; targets: unknown[] } => {
        if (typeof sourceNode === 'number' && isSplitNumericField(parentKey)) {
          const allocations = apportion(
            sourceNode,
            sourcePlans.map((p) => p.pct),
          );
          const sourceValue = Math.max(0, Math.round(sourceNode - allocations.reduce((s, v) => s + v, 0)));
          return {
            source: sourceValue,
            targets: allocations.map((a) => a),
          };
        }
        if (Array.isArray(sourceNode)) {
          const sourceArr = [...sourceNode];
          const targetsArr = targetNodes.map((n) => (Array.isArray(n) ? [...n] : []));
          for (let i = 0; i < sourceArr.length; i += 1) {
            const rec = walk(sourceArr[i], targetsArr.map((arr) => arr[i]), parentKey);
            sourceArr[i] = rec.source;
            rec.targets.forEach((v, idx) => {
              targetsArr[idx][i] = v;
            });
          }
          return { source: sourceArr, targets: targetsArr };
        }
        if (sourceNode && typeof sourceNode === 'object') {
          const sourceObj = { ...(sourceNode as Record<string, unknown>) };
          const targetsObj = targetNodes.map((n) => (n && typeof n === 'object' ? { ...(n as Record<string, unknown>) } : {}));
          Object.keys(sourceObj).forEach((k) => {
            const rec = walk(sourceObj[k], targetsObj.map((obj) => obj[k]), k);
            sourceObj[k] = rec.source;
            rec.targets.forEach((v, idx) => {
              targetsObj[idx][k] = v;
            });
          });
          return { source: sourceObj, targets: targetsObj };
        }
        return { source: sourceNode, targets: targetNodes };
      };

      const rec = walk(sourceOrder, targetOrders.map((t) => t.order));
      const sourceAfterSplit = rec.source as PreexecuteOrder;
      const targetsAfterSplit = rec.targets as PreexecuteOrder[];

      const firstTarget = targetOrders[0];
      if (firstTarget) {
        (sourceAfterSplit as Record<string, unknown>).division_info = {
          role: 'source',
          source_shop_id: state.order.shop_id,
          targets: targetOrders.map((target) => ({
            target_shop_id: target.plan.targetShopId,
            percentage: target.plan.pct,
          })),
        };
      }
      nextOrders.push({ order: sourceAfterSplit, status: 'pending' });
      nextLineStats[orderKey(sourceAfterSplit)] = summarizeOrderLineStats(sourceAfterSplit);
      targetsAfterSplit.forEach((order, idx) => {
        (order as Record<string, unknown>).division_info = {
          role: 'target',
          source_shop_id: state.order.shop_id,
          target_shop_id: targetOrders[idx]?.plan.targetShopId ?? order.shop_id,
          percentage: targetOrders[idx]?.plan.pct ?? 0,
        };
        nextOrders.push({
          order,
          status: 'pending',
        });
        nextLineStats[orderKey(order)] = summarizeOrderLineStats(order);
      });
    });

    setPreexecuteOrders(nextOrders);
    setOrderLineStatsByOrderKey(nextLineStats);
    setDivisionInfoByOrderKey(nextDivisionInfo);
    setAllOrdersExecuted(false);
  };

  const resetSplitPlans = () => {
    setSplitPlans([]);
    setSplitError(null);
    setPreexecuteOrders(basePreexecuteOrders.map((o) => ({ ...o, status: 'pending', error: undefined, result: undefined })));
    setDivisionInfoByOrderKey({});
    setOrderLineStatsByOrderKey(
      Object.fromEntries(
        basePreexecuteOrders.map((entry) => [orderKey(entry.order), summarizeOrderLineStats(entry.order)]),
      ),
    );
    setAllOrdersExecuted(false);
  };

  const handleExecuteOrder = async (index: number) => {
    const orderState = preexecuteOrders[index];
    if (!orderState || orderState.status === 'executing') return;

    // Update status to executing
    setPreexecuteOrders(prev => prev.map((o, i) => 
      i === index ? { ...o, status: 'executing' as const, error: undefined } : o
    ));

    try {
      const result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-export-weborder-execute',
        params: {
          'optim-order-data': orderState.order,
        },
      }) as any;

      const executionError = extractWeborderError(result);
      if (executionError) {
        setPreexecuteOrders(prev => prev.map((o, i) => 
          i === index ? { ...o, status: 'error' as const, error: executionError } : o
        ));
        return;
      }

      const payload = normalizeActionPayload(result);
      setPreexecuteOrders(prev => {
        const updated = prev.map((o, i) => 
          i === index ? { ...o, status: 'success' as const, result: payload?.data ?? payload } : o
        );
        // Check if all orders are now executed
        const allDone = updated.every(o => o.status === 'success' || o.status === 'error');
        if (allDone) {
          setAllOrdersExecuted(true);
        }
        return updated;
      });
    } catch (err) {
      setPreexecuteOrders(prev => prev.map((o, i) => 
        i === index ? { ...o, status: 'error' as const, error: err instanceof Error ? err.message : 'Errore sconosciuto' } : o
      ));
    }
  };

  const handleExecuteAll = async () => {
    const pendingIndexes = preexecuteOrders
      .map((o, i) => o.status === 'pending' ? i : -1)
      .filter(i => i !== -1);
    
    for (const index of pendingIndexes) {
      await handleExecuteOrder(index);
    }
  };

  const openOrderMetaEditor = (orderIndex: number) => {
    const state = preexecuteOrders[orderIndex];
    if (!state) return;
    setOrderMetaEditor({
      open: true,
      orderIndex,
      draftOrder: deepClone(state.order),
      title: `${state.order.n_ordine} - ${getShopDisplayName(state.order.shop_id, shopMapping ?? undefined)}`,
    });
  };

  const updateOrderMetaField = (field: string, value: string) => {
    setOrderMetaEditor((prev) => {
      if (!prev.draftOrder) return prev;
      const current = (prev.draftOrder as Record<string, unknown>)[field];
      let nextValue: unknown = value;
      if (typeof current === 'number') {
        const parsed = Number(value);
        nextValue = Number.isFinite(parsed) ? parsed : 0;
      } else if (typeof current === 'boolean') {
        nextValue = value === 'true';
      }
      return {
        ...prev,
        draftOrder: {
          ...prev.draftOrder,
          [field]: nextValue,
        },
      };
    });
  };

  const saveOrderMetaEditor = () => {
    if (orderMetaEditor.orderIndex === null || !orderMetaEditor.draftOrder) return;
    const targetIndex = orderMetaEditor.orderIndex;
    const nextOrder = orderMetaEditor.draftOrder;
    setPreexecuteOrders((prev) =>
      prev.map((entry, idx) => {
        if (idx !== targetIndex) return entry;
        return {
          ...entry,
          order: nextOrder,
          status: 'pending',
          error: undefined,
          result: undefined,
        };
      }),
    );
    setOrderMetaEditor({
      open: false,
      orderIndex: null,
      draftOrder: null,
      title: '',
    });
    setAllOrdersExecuted(false);
  };

  const handleClose = () => {
    setSchema(null);
    setCollectionMapping(null);
    setShopIds([]);
    setAllPossibleShopIds([]);
    setShopMapping(null);
    setValues({});
    setActiveShopTab('');
    setActiveSottoConsegnaTab('');
    setError(null);
    setPreexecuteOrders([]);
    setBasePreexecuteOrders([]);
    setPreexecuteError(null);
    setSplitPlans([]);
    setSplitError(null);
    setExpandedOrderSummaries(new Set());
    setOrderLineStatsByOrderKey({});
    setDivisionInfoByOrderKey({});
    setOrderMetaEditor({
      open: false,
      orderIndex: null,
      draftOrder: null,
      title: '',
    });
    setAllOrdersExecuted(false);
    onClose();
  };

  // When shop tab changes, reset the sotto_consegna tab to the first one of that shop
  const handleShopTabChange = (shopId: string) => {
    setActiveShopTab(shopId);
    if (schema && schema[shopId]) {
      const sottoConsegnas = Object.keys(schema[shopId]);
      if (sottoConsegnas.length > 0) {
        setActiveSottoConsegnaTab(sottoConsegnas[0]);
      }
    }
  };

  // DEBUG: Prefill all fields with mock data
  const handleDebugPrefill = () => {
    if (!schema) return;
    
    const mockValues: ValuesState = {};
    const today = new Date().toISOString().split('T')[0];
    const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    Object.keys(schema).forEach(shopId => {
      mockValues[shopId] = {};
      Object.keys(schema[shopId]).forEach(sottoConsegna => {
        mockValues[shopId][sottoConsegna] = {};
        schema[shopId][sottoConsegna].forEach(field => {
          let mockValue: string | number = field.default ?? '';
          
          // Generate mock values based on field name/type
          if (field.must_validate && !mockValue) {
            if (field.type === 'date') {
              if (field.name.includes('inizio')) {
                mockValue = today;
              } else if (field.name.includes('fine')) {
                mockValue = nextMonth;
              } else {
                mockValue = today;
              }
            } else if (field.type === 'int') {
              mockValue = 1;
            } else {
              // String fields - generate based on field name
              if (field.name === 'tipologia_ordine') {
                mockValue = 'STANDARD';
              } else if (field.name === 'cliente') {
                mockValue = 'CLIENTE_TEST_001';
              } else if (field.name === 'consegna' || field.name === 'codice_consegna') {
                mockValue = sottoConsegna;
              } else if (field.name === 'note_consegna') {
                mockValue = 'Test export - DEBUG';
              } else {
                mockValue = `MOCK_${field.name.toUpperCase()}`;
              }
            }
          }
          
          mockValues[shopId][sottoConsegna][field.name] = {
            value: mockValue,
            validated: true, // Auto-validate all mock data
            modified: true,
          };
        });
      });
    });
    
    setValues(mockValues);
    console.log('DEBUG: Prefilled with mock data', mockValues);
  };

  const renderField = (shopId: string, sottoConsegna: string, field: FieldSchema) => {
    const fieldValue = values[shopId]?.[sottoConsegna]?.[field.name];
    const isEditable = field.editable !== false;
    const requiresValidation = field.must_validate === true;
    const isValidated = fieldValue?.validated ?? false;
    const isModified = fieldValue?.modified ?? false;
    const currentValue = fieldValue?.value ?? '';
    const hasValidValue = isFieldValueValid(currentValue, field.type);
    // SPJ-1363: stato della GET date consegna (solo per il campo 'consegna').
    const isConsegna = field.name === 'consegna';
    const dKey = deliveryKey(shopId, sottoConsegna);
    const isDeliveryLoading = isConsegna && !!deliveryLoading[dKey];
    const deliveryErr = isConsegna ? deliveryError[dKey] : undefined;

    return (
      <div key={field.name} className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-b-0">
        {/* Label */}
        <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
          {field.description || field.name}
          {requiresValidation && <span className="ml-1" style={{ color: COLORS.warning }}>*</span>}
        </label>
        
        {/* Input */}
        <div className="flex-1">
          {isEditable ? (
            <input
              type={field.type === 'int' ? 'number' : field.type === 'date' ? 'date' : 'text'}
              value={currentValue}
              onChange={(e) => handleFieldChange(shopId, sottoConsegna, field.name, field.type === 'int' ? parseInt(e.target.value) || 0 : e.target.value, field.type)}
              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1"
              style={{
                borderColor: requiresValidation && !isValidated ? COLORS.warning : '#d1d5db',
                backgroundColor: requiresValidation && !isValidated ? '#fef7f4' : 'white',
              }}
            />
          ) : (
            <div className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 text-gray-600">
              {currentValue}
            </div>
          )}
          {/* SPJ-1363: messaggio d'errore per il codice consegna sotto l'input */}
          {deliveryErr && (
            <div className="mt-1 text-xs" style={{ color: COLORS.error }}>
              {deliveryErr}
            </div>
          )}
        </div>

        {/* Validation button/status */}
        <div className="w-28 flex-shrink-0">
          {requiresValidation && (
            isDeliveryLoading ? (
              // SPJ-1363: spinner mentre la BASP risponde con le date della consegna
              <span className="text-xs flex items-center gap-1 text-gray-500">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: COLORS.primary }}></span>
                Verifico…
              </span>
            ) : deliveryErr ? (
              // SPJ-1363: consegna non trovata -> errore rosso al posto di "Validato"
              <span className="text-xs flex items-center gap-1" style={{ color: COLORS.error }}>
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Errore
              </span>
            ) : isValidated ? (
              <span className="text-xs flex items-center gap-1" style={{ color: COLORS.success }}>
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {isModified ? 'Modificato' : 'Validato'}
              </span>
            ) : hasValidValue ? (
              <button
                onClick={() => handleValidate(shopId, sottoConsegna, field.name)}
                className="px-2 py-1 text-white text-xs rounded transition-colors"
                style={{ backgroundColor: COLORS.primary }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primaryLight}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.primary}
              >
                Valida
              </button>
            ) : (
              <span className="text-gray-400 text-xs italic">
                Inserisci valore
              </span>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-800">
              Esporta su Weborder
            </h2>
            {/* DEBUG button - uncomment to enable mock fill
            {schema && preexecuteOrders.length === 0 && (
              <button
                onClick={handleDebugPrefill}
                className="px-2 py-1 text-xs rounded border border-dashed border-orange-400 text-orange-600 hover:bg-orange-50"
                title="DEBUG: Prefill with mock data"
              >
                🔧 Mock Fill
              </button>
            )}
            */}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: COLORS.primary }}></div>
            </div>
          )}

          {error && (
            <div className="rounded p-4 mb-4" style={{ backgroundColor: '#fdf5f4', border: `1px solid ${COLORS.error}` }}>
              <div className="flex items-center gap-2" style={{ color: COLORS.error }}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Errore:</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          {preexecuteError && (
            <div className="rounded p-4 mb-4" style={{ backgroundColor: '#fdf5f4', border: `1px solid ${COLORS.error}` }}>
              <div className="flex items-center gap-2" style={{ color: COLORS.error }}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Errore:</span>
                <span>{preexecuteError}</span>
              </div>
            </div>
          )}

          {/* Orders table after preexecute */}
          {preexecuteOrders.length > 0 && (
            <div>
              <div className="mb-4 rounded border p-4" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-semibold" style={{ color: COLORS.primary }}>Ripartizione Multi-shop (percentuale)</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={addSplitPlan}
                      className="px-3 py-1 text-xs rounded border"
                      style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                    >
                      + Aggiungi Split
                    </button>
                    <button
                      onClick={applySplitPlans}
                      disabled={splitPlans.length === 0}
                      className="px-3 py-1 text-xs rounded text-white disabled:opacity-50"
                      style={{ backgroundColor: COLORS.primary }}
                    >
                      Applica Ripartizione
                    </button>
                    <button
                      onClick={resetSplitPlans}
                      disabled={basePreexecuteOrders.length === 0}
                      className="px-3 py-1 text-xs rounded border"
                      style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                <p className="mb-2 text-xs" style={{ color: COLORS.secondary }}>
                  Definisci per ordine/consegna quale percentuale inviare a un altro shop. Dopo l'applicazione puoi rifinire le taglie per ogni ordine.
                </p>
                {splitError && (
                  <div className="mb-2 text-xs" style={{ color: COLORS.error }}>{splitError}</div>
                )}
                {splitPlans.length === 0 && (
                  <div className="text-xs" style={{ color: COLORS.secondary }}>
                    Nessuna ripartizione impostata.
                  </div>
                )}
                {splitPlans.map((plan) => {
                  const source = basePreexecuteOrders.find((o) => orderKey(o.order) === plan.sourceOrderKey)?.order;
                  return (
                    <div key={plan.id} className="mb-2 grid grid-cols-12 items-center gap-2">
                      <div className="col-span-5">
                        <select
                          value={plan.sourceOrderKey}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSplitPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, sourceOrderKey: value } : p)));
                          }}
                          className="w-full rounded border px-2 py-1 text-xs"
                        >
                          {basePreexecuteOrders.map((o) => (
                            <option key={orderKey(o.order)} value={orderKey(o.order)}>
                              {o.order.n_ordine} | {getShopDisplayName(o.order.shop_id, shopMapping ?? undefined)} | {o.order.sotto_consegna}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3">
                        <select
                          value={plan.targetShopId}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSplitPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, targetShopId: value } : p)));
                          }}
                          className="w-full rounded border px-2 py-1 text-xs"
                        >
                          {allPossibleShopIds.map((id) => (
                            <option key={id} value={id} disabled={id === source?.shop_id}>
                              {getShopDisplayName(id, shopMapping ?? undefined)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={99}
                          value={plan.percentage}
                          onChange={(e) => {
                            const value = Number(e.target.value) || 0;
                            setSplitPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, percentage: Math.min(99, Math.max(0, value)) } : p)));
                          }}
                          className="w-full rounded border px-2 py-1 text-xs"
                        />
                        <span className="text-xs" style={{ color: COLORS.secondary }}>%</span>
                      </div>
                      <div className="col-span-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openSplitEditor(plan.id)}
                            className="px-2 py-1 text-xs rounded border"
                            style={{ borderColor: COLORS.secondaryLight, color: COLORS.primary }}
                          >
                            Split
                          </button>
                          <button
                            onClick={() => setSplitPlans((prev) => prev.filter((p) => p.id !== plan.id))}
                            className="px-2 py-1 text-xs rounded border"
                            style={{ borderColor: COLORS.errorLight, color: COLORS.error }}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2" style={{ color: COLORS.primary }}>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="font-semibold text-lg">Ordini pronti per l'esportazione</span>
                </div>
                {!allOrdersExecuted && preexecuteOrders.some(o => o.status === 'pending') && (
                  <button
                    onClick={handleExecuteAll}
                    className="px-4 py-2 rounded font-medium text-white flex items-center gap-2 transition-colors"
                    style={{ backgroundColor: COLORS.primary }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primaryLight}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.primary}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Esegui Mancanti ({preexecuteOrders.filter(o => o.status === 'pending').length})
                  </button>
                )}
              </div>

              <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-4">
                <div className="rounded border px-3 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div style={{ color: COLORS.secondary }}>Ordini da esportare</div>
                  <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>{preexecuteOrders.length}</div>
                </div>
                <div className="rounded border px-3 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div style={{ color: COLORS.secondary }}>Righe ordine totali</div>
                  <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                    {preexecuteOrders.reduce((sum, state) => sum + (orderLineStatsByOrderKey[orderKey(state.order)]?.linesTotal ?? 0), 0)}
                  </div>
                </div>
                <div className="rounded border px-3 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div style={{ color: COLORS.secondary }}>Righe attive (capi &gt; 0)</div>
                  <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                    {preexecuteOrders.reduce((sum, state) => sum + (orderLineStatsByOrderKey[orderKey(state.order)]?.linesWithCapi ?? 0), 0)}
                  </div>
                </div>
                <div className="rounded border px-3 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div style={{ color: COLORS.secondary }}>Righe create/modificate</div>
                  <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                    {preexecuteOrders.reduce(
                      (sum, state) =>
                        sum + ((orderLineStatsByOrderKey[orderKey(state.order)]?.linesActivated ?? 0) + (orderLineStatsByOrderKey[orderKey(state.order)]?.linesChanged ?? 0)),
                      0,
                    )}
                  </div>
                </div>
              </div>

              {allOrdersExecuted && (
                <div className="rounded p-4 mb-4" style={{ backgroundColor: COLORS.background, border: `1px solid ${COLORS.success}` }}>
                  <div className="flex items-center gap-2" style={{ color: COLORS.success }}>
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Tutti gli ordini sono stati processati!</span>
                  </div>
                </div>
              )}

              <div className="border rounded overflow-hidden" style={{ borderColor: COLORS.errorLight }}>
                <table className="w-full">
                  <thead style={{ backgroundColor: COLORS.background }}>
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: COLORS.primary }}>N° Ordine</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: COLORS.primary }}>Negozio</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: COLORS.primary }}>Consegna</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: COLORS.primary }}>Stato</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold" style={{ color: COLORS.primary }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preexecuteOrders.map((orderState, index) => {
                      const { order, status, error: orderError } = orderState;
                      const shopName = getShopDisplayName(order.shop_id, shopMapping ?? undefined);
                      // La mappa delle consegne e' indicizzata per sotto-consegna, non per
                      // il codice scelto dall'utente.
                      const consegnaName = collectionMapping?.[order.sotto_consegna]?.name || order.sotto_consegna;
                      const key = orderKey(order);
                      const lineStats = orderLineStatsByOrderKey[key] ?? summarizeOrderLineStats(order);
                      const divisionInfo = divisionInfoByOrderKey[key];
                      const isSummaryOpen = expandedOrderSummaries.has(key);
                      
                      return (
                        <Fragment key={`${order.shop_id}-${order.sotto_consegna}-${order.n_ordine}`}>
                          <tr 
                            className="border-t"
                            style={{ borderColor: COLORS.errorLight }}
                          >
                            <td className="px-4 py-3 text-sm font-mono" style={{ color: COLORS.primary }}>
                              {order.n_ordine}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-2">
                                <svg className="h-4 w-4" style={{ color: COLORS.successLight }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                {shopName}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: COLORS.secondary }}>
                              {consegnaName}
                            </td>
                            <td className="px-4 py-3">
                              {status === 'pending' && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ backgroundColor: COLORS.background, color: COLORS.successLight }}>
                                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  In attesa
                                </span>
                              )}
                              {status === 'executing' && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ backgroundColor: '#eef4ff', color: COLORS.secondary }}>
                                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Esecuzione...
                                </span>
                              )}
                              {status === 'success' && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ backgroundColor: '#f0f5f0', color: COLORS.success }}>
                                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Completato
                                </span>
                              )}
                              {status === 'error' && (
                                <div>
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ backgroundColor: '#fdf5f4', color: COLORS.error }}>
                                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    Errore
                                  </span>
                                  {orderError && (
                                    <div className="text-xs mt-1" style={{ color: COLORS.error }}>{orderError}</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => {
                                    setExpandedOrderSummaries((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    });
                                  }}
                                  className="px-2 py-1 text-xs rounded border"
                                  style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                                >
                                  {isSummaryOpen ? 'Nascondi' : 'Summary'}
                                </button>
                                {status !== 'executing' && (
                                  <button
                                    onClick={() => openOrderMetaEditor(index)}
                                    className="px-2 py-1 text-xs rounded border"
                                    style={{ borderColor: COLORS.secondaryLight, color: COLORS.primary }}
                                  >
                                    Dati
                                  </button>
                                )}
                                {status === 'pending' && (
                                  <button
                                    onClick={() => handleExecuteOrder(index)}
                                    className="px-3 py-1 text-xs rounded text-white transition-colors"
                                    style={{ backgroundColor: COLORS.primary }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.primaryLight}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.primary}
                                  >
                                    Esegui
                                  </button>
                                )}
                                {status === 'error' && (
                                  <button
                                    onClick={() => handleExecuteOrder(index)}
                                    className="px-3 py-1 text-xs rounded text-white transition-colors"
                                    style={{ backgroundColor: COLORS.warning }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.warningLight}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = COLORS.warning}
                                  >
                                    Riprova
                                  </button>
                                )}
                                {status === 'success' && (
                                  <span className="text-xs" style={{ color: COLORS.successLight }}>—</span>
                                )}
                                {status === 'executing' && (
                                  <span className="text-xs" style={{ color: COLORS.secondary }}>...</span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isSummaryOpen && (
                            <tr className="border-t" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                              <td colSpan={5} className="px-4 py-3">
                                <div className="grid grid-cols-2 gap-4 text-xs">
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Righe totali</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.linesTotal}</div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Righe attive (capi &gt; 0)</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.linesWithCapi}</div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Totale capi</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.totalCapi}</div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Righe modificate</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.linesChanged}</div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Righe create</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.linesActivated}</div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.secondary }}>Righe azzerate</div>
                                    <div className="font-semibold" style={{ color: COLORS.primary }}>{lineStats.linesDeactivated}</div>
                                  </div>
                                </div>
                                {divisionInfo && (
                                  <div className="mt-3 rounded border px-2 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: '#fff' }}>
                                    <div style={{ color: COLORS.secondary }}>Divisione taglie salvata</div>
                                    <div className="font-medium" style={{ color: COLORS.primary }}>
                                      {divisionInfo.role === 'source' ? 'Origine' : 'Destinazione'} | {divisionInfo.sourceShopId} → {divisionInfo.targetShopId} ({divisionInfo.percentage}%)
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {orderMetaEditor.open && orderMetaEditor.orderIndex !== null && orderMetaEditor.draftOrder && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-lg border bg-white shadow-2xl" style={{ borderColor: COLORS.errorLight }}>
                <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>Modifica dati ordine prima dell'export</div>
                    <div className="text-xs" style={{ color: COLORS.secondary }}>{orderMetaEditor.title}</div>
                  </div>
                  <button
                    onClick={() => setOrderMetaEditor({ open: false, orderIndex: null, draftOrder: null, title: '' })}
                    className="text-sm"
                    style={{ color: COLORS.secondary }}
                  >
                    Chiudi
                  </button>
                </div>
                <div className="max-h-[58vh] overflow-auto p-4">
                  <div className="mb-3 text-xs" style={{ color: COLORS.secondary }}>
                    Modifica i campi header/payload (es. data ordine, cliente). Le righe SKU/taglie non sono editabili qui.
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(orderMetaEditor.draftOrder)
                      .filter(([key, value]) => !EDITOR_HIDDEN_KEYS.has(key) && isEditableOrderFieldValue(value))
                      .map(([key, value]) => (
                        <label key={key} className="flex items-center gap-3 text-xs">
                          <span className="w-48 truncate font-medium" style={{ color: COLORS.primary }}>{key}</span>
                          {typeof value === 'boolean' ? (
                            <select
                              value={String(value)}
                              onChange={(e) => updateOrderMetaField(key, e.target.value)}
                              className="flex-1 rounded border px-2 py-1"
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : (
                            <input
                              type={typeof value === 'number' ? 'number' : 'text'}
                              value={String(value)}
                              onChange={(e) => updateOrderMetaField(key, e.target.value)}
                              className="flex-1 rounded border px-2 py-1"
                            />
                          )}
                        </label>
                      ))}
                  </div>
                  {Object.entries(orderMetaEditor.draftOrder).some(([key]) => NON_EDITABLE_ORDER_KEYS.has(key)) && (
                    <div className="mt-4 rounded border px-3 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                      <span style={{ color: COLORS.secondary }}>Campi non editabili in questo pannello: </span>
                      <span style={{ color: COLORS.primary }}>righe</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: COLORS.errorLight }}>
                  <button
                    onClick={() => setOrderMetaEditor({ open: false, orderIndex: null, draftOrder: null, title: '' })}
                    className="rounded border px-3 py-1 text-xs"
                    style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                  >
                    Annulla
                  </button>
                  <button
                    onClick={saveOrderMetaEditor}
                    className="rounded px-3 py-1 text-xs text-white"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    Salva modifiche
                  </button>
                </div>
              </div>
            </div>
          )}

          {splitEditor.open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-6xl max-h-[85vh] overflow-hidden rounded-lg border bg-white shadow-2xl" style={{ borderColor: COLORS.errorLight }}>
                {(() => {
                  const splitRows = buildSplitEditorRows(splitEditor.baseSkuTaglie);
                  return (
                    <>
                <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: COLORS.errorLight, backgroundColor: COLORS.background }}>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: COLORS.primary }}>
                      Split Taglie per Shop
                    </div>
                    <div className="text-xs" style={{ color: COLORS.secondary }}>
                      {getShopDisplayName(splitEditor.sourceShopId, shopMapping ?? undefined)} → {getShopDisplayName(splitEditor.targetShopId, shopMapping ?? undefined)} ({splitEditor.percentage}%)
                    </div>
                  </div>
                  <button
                    onClick={() => setSplitEditor((prev) => ({ ...prev, open: false }))}
                    className="text-sm"
                    style={{ color: COLORS.secondary }}
                  >
                    Chiudi
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 border-b px-4 py-2 text-xs" style={{ borderColor: COLORS.errorLight, backgroundColor: '#f8fafc' }}>
                  <div>
                    <div style={{ color: COLORS.secondary }}>Totale shop origine</div>
                    <div className="font-semibold" style={{ color: COLORS.primary }}>
                      {sumSkuTaglie(buildSourceFromTargetAllocation(splitEditor.baseSkuTaglie, splitEditor.targetAllocation))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: COLORS.secondary }}>Totale shop destinazione</div>
                    <div className="font-semibold" style={{ color: COLORS.primary }}>
                      {sumSkuTaglie(splitEditor.targetAllocation)}
                    </div>
                  </div>
                </div>

                <div className="max-h-[56vh] overflow-auto p-4">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="text-left text-xs" style={{ color: COLORS.primary }}>
                        <th className="border-b px-2 py-2" style={{ borderColor: COLORS.errorLight }}>SKU</th>
                        <th className="border-b px-2 py-2" style={{ borderColor: COLORS.errorLight }}>Taglia</th>
                        <th className="border-b px-2 py-2 text-center" style={{ borderColor: COLORS.errorLight }}>
                          {getShopDisplayName(splitEditor.sourceShopId, shopMapping ?? undefined)}
                        </th>
                        <th className="border-b px-2 py-2 text-center" style={{ borderColor: COLORS.errorLight }}>Azioni</th>
                        <th className="border-b px-2 py-2 text-center" style={{ borderColor: COLORS.errorLight }}>
                          {getShopDisplayName(splitEditor.targetShopId, shopMapping ?? undefined)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {splitRows.map(({ sku, taglia, baseQty, groupIndex }, idx) => {
                          const targetQty = Math.max(0, Math.round(splitEditor.targetAllocation[sku]?.[taglia] ?? 0));
                          const sourceQty = Math.max(0, Math.round(baseQty - targetQty));
                          const rowBackground = groupIndex % 2 === 0 ? '#f3f4f6' : '#ffffff';
                          return (
                            <tr key={`${sku}-${taglia}-${idx}`} className="text-xs" style={{ backgroundColor: rowBackground }}>
                              <td className="border-b px-2 py-2 font-medium" style={{ borderColor: COLORS.errorLight, color: COLORS.primary }}>{sku}</td>
                              <td className="border-b px-2 py-2" style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}>{taglia}</td>
                              <td className="border-b px-2 py-2 text-center font-semibold" style={{ borderColor: COLORS.errorLight, color: COLORS.primary }}>{sourceQty}</td>
                              <td className="border-b px-2 py-2" style={{ borderColor: COLORS.errorLight }}>
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => moveTagliaOneUnit(sku, taglia, 'target-to-source')}
                                    disabled={targetQty <= 0}
                                    className="rounded border px-2 py-0.5 text-[11px] disabled:opacity-40"
                                    style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                                  >
                                    ←
                                  </button>
                                  <button
                                    onClick={() => moveTagliaOneUnit(sku, taglia, 'source-to-target')}
                                    disabled={sourceQty <= 0}
                                    className="rounded border px-2 py-0.5 text-[11px] disabled:opacity-40"
                                    style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                                  >
                                    →
                                  </button>
                                </div>
                              </td>
                              <td className="border-b px-2 py-2 text-center font-semibold" style={{ borderColor: COLORS.errorLight, color: COLORS.primary }}>{targetQty}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: COLORS.errorLight }}>
                  <button
                    onClick={() => setSplitEditor((prev) => ({ ...prev, open: false }))}
                    className="rounded border px-3 py-1 text-xs"
                    style={{ borderColor: COLORS.errorLight, color: COLORS.secondary }}
                  >
                    Annulla
                  </button>
                  <button
                    onClick={saveSplitEditor}
                    className="rounded px-3 py-1 text-xs text-white"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    Salva Split
                  </button>
                </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {schema && preexecuteOrders.length === 0 && (
            <>
              {/* SPJ-892: raggruppamenti negozi aggregati (uniforme all'export Excel) */}
              {aggregatedShops.length > 0 && (
                <div className="mb-4 rounded border border-gray-200 p-3 text-sm" style={{ backgroundColor: COLORS.background }}>
                  <div className="font-semibold text-gray-900">Raggruppamenti negozi (opzionale)</div>
                  <div className="text-gray-600 mb-2">
                    Assegna la stessa etichetta a più negozi per esportarli come un unico ordine (budget sommato).
                    Lascia vuoto per tenere il negozio separato. Premi "Applica" per aggiornare i tab.
                  </div>
                  {aggregatedShops.map((agg) => (
                    <div key={agg.sourceId} className="space-y-1.5 mb-2">
                      {aggregatedShops.length > 1 && (
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{agg.members.map((m) => m.label).join(' + ')}</div>
                      )}
                      {agg.members.map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-3">
                          <span className="min-w-0 flex-1 truncate text-gray-800" title={m.label}>{m.label}</span>
                          <input
                            type="text"
                            value={shopGroups[m.id] || ''}
                            onChange={(e) => setShopGroups((prev) => ({ ...prev, [m.id]: e.target.value }))}
                            placeholder="gruppo"
                            className="w-24 flex-shrink-0 rounded border border-gray-300 px-2 py-1 text-right focus:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <button
                      onClick={() => { void applyShopGroups(); }}
                      disabled={loading || JSON.stringify(shopGroups) === JSON.stringify(appliedShopGroups)}
                      className="rounded px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                      style={{ backgroundColor: COLORS.primary }}
                    >
                      Applica raggruppamenti
                    </button>
                  </div>
                </div>
              )}
              {/* Shop tabs - first level */}
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: COLORS.successLight }}>Negozio</div>
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-2 overflow-x-auto">
                    {shopIds.map(shopId => {
                      const shopValid = isShopValidated(shopId);
                      const isActive = activeShopTab === shopId;
                      return (
                        <button
                          key={shopId}
                          onClick={() => handleShopTabChange(shopId)}
                          className="py-3 px-5 border-b-2 font-semibold text-base whitespace-nowrap flex items-center gap-2 transition-colors"
                          style={{
                            borderColor: isActive ? COLORS.primary : 'transparent',
                            color: isActive ? COLORS.primary : '#6b7280',
                            backgroundColor: isActive ? COLORS.background : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.color = COLORS.primary;
                              e.currentTarget.style.borderColor = COLORS.errorLight;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.color = '#6b7280';
                              e.currentTarget.style.borderColor = 'transparent';
                            }
                          }}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {getShopDisplayName(shopId, shopMapping ?? undefined)}
                          {!shopValid && (
                            <span 
                              className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full" 
                              style={{ backgroundColor: COLORS.warning }}
                              title="Validazione richiesta"
                            >
                              !
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              {/* Sotto-consegna tabs - second level */}
              {activeShopTab && schema[activeShopTab] && (
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: COLORS.successLight }}>Sotto-consegna</div>
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-4 overflow-x-auto">
                      {Object.keys(schema[activeShopTab]).map(sottoConsegna => {
                        const tabValid = isSottoConsegnaValidated(activeShopTab, sottoConsegna);
                        const collectionInfo = collectionMapping?.[sottoConsegna];
                        const isActive = activeSottoConsegnaTab === sottoConsegna;
                        return (
                          <button
                            key={sottoConsegna}
                            onClick={() => setActiveSottoConsegnaTab(sottoConsegna)}
                            className="py-2 px-4 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-1 transition-colors"
                            style={{
                              borderColor: isActive ? COLORS.secondary : 'transparent',
                              color: isActive ? COLORS.secondary : '#6b7280',
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = COLORS.secondary;
                                e.currentTarget.style.borderColor = COLORS.errorLight;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = '#6b7280';
                                e.currentTarget.style.borderColor = 'transparent';
                              }
                            }}
                            title={collectionInfo ? `${collectionInfo.name} (${collectionInfo.data_inizio_consegna} - ${collectionInfo.data_fine_consegna})` : undefined}
                          >
                            {collectionInfo?.codename || sottoConsegna}
                            {!tabValid && (
                              <span 
                                className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full" 
                                style={{ backgroundColor: COLORS.warning }}
                                title="Validazione richiesta"
                              >
                                !
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </nav>
                  </div>
                </div>
              )}

              {/* Fields for active tab */}
              {activeShopTab && activeSottoConsegnaTab && schema[activeShopTab]?.[activeSottoConsegnaTab] && (
                <div>
                  <div 
                    className="mb-3 p-2 rounded text-xs"
                    style={{ backgroundColor: COLORS.background, border: `1px solid ${COLORS.errorLight}`, color: COLORS.primary }}
                  >
                    <strong>Negozio:</strong> {getShopDisplayName(activeShopTab, shopMapping ?? undefined)} | <strong>Sotto-consegna:</strong> {collectionMapping?.[activeSottoConsegnaTab]?.name || activeSottoConsegnaTab}
                    <br />
                    I campi con <span style={{ color: COLORS.warning }} className="font-bold">*</span> richiedono validazione. 
                    Verifica i valori e clicca "Valida" o modificali.
                  </div>
                  
                  <div className="rounded p-3">
                    {schema[activeShopTab][activeSottoConsegnaTab].map(field => renderField(activeShopTab, activeSottoConsegnaTab, field))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - form validation phase */}
        {schema && preexecuteOrders.length === 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center gap-8">
            <div className="text-sm">
              {isAllValidated() ? (
                <span className="font-medium flex items-center gap-1" style={{ color: COLORS.success }}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Tutti i campi validati
                </span>
              ) : (
                <span className="font-medium" style={{ color: COLORS.warning }}>
                  Valida tutti i campi obbligatori
                </span>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleClose}
                className="px-5 py-2 border rounded transition-colors"
                style={{ borderColor: COLORS.errorLight, color: COLORS.success }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.background}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Annulla
              </button>
              <button
                onClick={handlePreexecute}
                disabled={!isAllValidated() || executing}
                className="px-8 py-2 rounded font-medium transition-colors text-white"
                style={{ 
                  backgroundColor: isAllValidated() && !executing ? COLORS.primary : '#d1d5db',
                  color: isAllValidated() && !executing ? 'white' : '#6b7280',
                  cursor: isAllValidated() && !executing ? 'pointer' : 'not-allowed'
                }}
                onMouseEnter={(e) => {
                  if (isAllValidated() && !executing) {
                    e.currentTarget.style.backgroundColor = COLORS.primaryLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (isAllValidated() && !executing) {
                    e.currentTarget.style.backgroundColor = COLORS.primary;
                  }
                }}
              >
                {executing ? 'Preparazione...' : 'Prepara Ordini'}
              </button>
            </div>
          </div>
        )}

        {/* Footer - orders execution phase */}
        {preexecuteOrders.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center gap-8">
            <div className="text-sm">
              {allOrdersExecuted ? (
                <span className="font-medium flex items-center gap-1" style={{ color: COLORS.success }}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Esportazione completata
                </span>
              ) : (
                <span style={{ color: COLORS.secondary }}>
                  {preexecuteOrders.filter(o => o.status === 'success').length} / {preexecuteOrders.length} ordini eseguiti
                </span>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleClose}
                className="px-5 py-2 border rounded transition-colors"
                style={{ borderColor: COLORS.errorLight, color: COLORS.success }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.background}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {allOrdersExecuted ? 'Chiudi' : 'Annulla'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
